'use client';

import clsx from 'clsx';
import { CSSProperties, PropsWithChildren } from 'react';
import {
  CSS_VAR_DESKTOP_SCALE,
  CSS_VAR_FONT_SIZE,
  CSS_VAR_FONT_WEIGHT,
  CSS_VAR_LINE_HEIGHT,
} from '../../constants/css';
import { convertBemToCamel } from '../../helpers';
import { Alignment } from '../../types/alignment';
import { BrandColor } from '../../types/brand-color';
import styles from './Text.module.scss';

export type TextProps = PropsWithChildren<{
  size?: number;
  desktopScale?: number;
  lineHeight?: number;
  variant?: 'block' | 'inline';
  hyphens?: 'none' | 'auto';
  align?: Alignment;
  color?: BrandColor;
  gradient?: boolean;
  className?: string;
  weight?: number;
  width?: 'full' | 'm' | 's';
}>;

export const Text = ({
  children,
  variant,
  hyphens = 'none',
  size,
  desktopScale,
  lineHeight,
  align = 'left',
  color,
  gradient = false,
  className,
  weight,
  width = 'full',
}: TextProps) => {
  const classes = clsx(
    styles.base,
    className,
    {
      [styles.colorGradient]: gradient,
      [styles[convertBemToCamel(`align-${align}`)]]: !!align,
      [styles[convertBemToCamel(`hyphens-${hyphens}`)]]: !!hyphens,
      [styles[convertBemToCamel(`width-${width}`)]]: width !== 'full',
    },
    color && styles[convertBemToCamel(`color-${color}`)]
  );

  const style = {
    [CSS_VAR_FONT_SIZE]: size,
    [CSS_VAR_DESKTOP_SCALE]: desktopScale,
    [CSS_VAR_LINE_HEIGHT]: lineHeight,
    [CSS_VAR_FONT_WEIGHT]: weight,
  } as CSSProperties;

  const props = { style, className: classes };

  switch (variant) {
    case 'inline':
      return <span {...props}>{children}</span>;
    case 'block':
    default:
      return <p {...props}>{children}</p>;
  }
};
